import { convertCommercialQuantityUsingSnapshot, hasPersistedQuantitySet, isAllocationUnit, quantitySetFromFields } from '../../utils/commercialQuantity';
import type { AllocationUnit } from '../../utils/dimensionConversion';
import { normalizeUnitCode } from '../../utils/units';
import { WAREHOUSE_CONVERTED_QUANTITY_MAX } from '../warehouse/warehouseQuantityConversion';

export type WZBatchConversionTarget = 'pcs' | 'm2' | 'm3';

export interface WZBatchConvertibleItem {
  quantity?: number | null;
  unit?: string | null;
  quantityPcs?: number | null;
  quantityM2?: number | null;
  quantityM3?: number | null;
  conversionUnit?: 'pcs' | 'm2' | 'm3' | null;
  saleQuantity?: number | null;
  saleUnit?: string | null;
  width?: number | null;
  height?: number | null;
  length?: number | null;
  thickness?: number | null;
}

interface WZBatchConversionResult<T extends WZBatchConvertibleItem> {
  items: T[];
  convertedCount: number;
  skippedCount: number;
}

const commercialUnit = (unit: string | null | undefined): AllocationUnit | null => {
  const normalized = normalizeUnitCode(unit);
  return isAllocationUnit(normalized) ? normalized : null;
};

/**
 * Apply one commercial quantity basis to every WZ line without changing the
 * physical stock movement. Each line uses its own persisted dimensions.
 * Unsupported/missing-dimension lines stay byte-for-byte unchanged.
 */
export function convertWZItemsToTarget<T extends WZBatchConvertibleItem>(
  items: T[],
  target: WZBatchConversionTarget,
): WZBatchConversionResult<T> {
  let convertedCount = 0;
  let skippedCount = 0;

  const nextItems = items.map((item) => {
    const physicalUnit = commercialUnit(item.unit);
    const pricingUnit = target;
    const physicalQuantity = Number(item.quantity);

    if (!physicalUnit || !Number.isFinite(physicalQuantity) || physicalQuantity <= 0) {
      skippedCount += 1;
      return item;
    }

    if (pricingUnit === physicalUnit) {
      convertedCount += 1;
      return {
        ...item,
        conversionUnit: null,
        saleQuantity: physicalQuantity,
        saleUnit: physicalUnit,
      };
    }

    const converted = convertCommercialQuantityUsingSnapshot(
      physicalQuantity,
      physicalUnit,
      pricingUnit,
      item,
      hasPersistedQuantitySet(item as Record<string, unknown>) ? quantitySetFromFields(item as Record<string, unknown>) : null,
    );

    if (
      converted.error
      || converted.value == null
      || converted.value <= 0
      || converted.value > WAREHOUSE_CONVERTED_QUANTITY_MAX
    ) {
      skippedCount += 1;
      return item;
    }

    convertedCount += 1;
    return {
      ...item,
      conversionUnit: pricingUnit === 'pcs' ? null : pricingUnit,
      saleQuantity: converted.value,
      saleUnit: pricingUnit,
    };
  });

  return { items: nextItems, convertedCount, skippedCount };
}
